import * as z from "zod";
import type { TFunction } from "i18next";

import type { AdminUserType } from "../types";

// Domínio corporativo obrigatório para usuários `tellus` (espelha a regra do
// backend em create-admin-user.use-case; catálogo 3.6.69). Ver
// docs/prd/admin/admin.md §5.2.
export const TELLUS_EMAIL_DOMAIN_SUFFIX = "@telluscompany.com.br";

export const adminUserFilterSchema = z.object({
  name: z.string().default(""),
  email: z.string().default(""),
});

export type AdminUserFilterData = z.infer<typeof adminUserFilterSchema>;

interface CreateAdminUserSchemaOptions {
  includeUserTypeField: boolean;
  forceTellusEmailMode: boolean;
}

const adminUserTypeSchema = z.enum(["client", "tellus"]);

/** Garante que o valor do input sempre termina com o domínio Tellus. */
export function clampTellusEmailValue(raw: string): string {
  const suffix = TELLUS_EMAIL_DOMAIN_SUFFIX;

  if (raw.endsWith(suffix)) {
    return raw;
  }

  const atIndex = raw.indexOf("@");
  const localPart = atIndex >= 0 ? raw.slice(0, atIndex) : raw;

  return `${localPart}${suffix}`;
}

export function tellusEmailLocalPart(email: string): string {
  return email.slice(0, -TELLUS_EMAIL_DOMAIN_SUFFIX.length);
}

export const createAdminUserSchema = (
  t: TFunction,
  options: CreateAdminUserSchemaOptions,
) =>
  z
    .object({
      name: z.string().min(2, t("admin.users.validation.nameRequired")),
      phone: z.string().min(8, t("admin.users.validation.phoneRequired")),
      department: z.string().optional(),
      position: z.string().optional(),
      userType: options.includeUserTypeField
        ? adminUserTypeSchema
        : adminUserTypeSchema.optional(),
      email: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const tellusEmailMode =
        options.forceTellusEmailMode || data.userType === "tellus";

      if (tellusEmailMode) {
        const email = data.email?.trim() ?? "";
        if (!email.endsWith(TELLUS_EMAIL_DOMAIN_SUFFIX)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["email"],
            message: t("admin.users.validation.emailTellusDomain"),
          });
          return;
        }
        const localPart = tellusEmailLocalPart(email).trim();
        if (!localPart) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["email"],
            message: t("admin.users.validation.emailRequired"),
          });
          return;
        }
        if (!z.string().email().safeParse(email).success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["email"],
            message: t("admin.users.validation.emailInvalid"),
          });
        }
        return;
      }

      const email = data.email?.trim() ?? "";
      if (!email) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["email"],
          message: t("admin.users.validation.emailRequired"),
        });
        return;
      }
      if (!z.string().email().safeParse(email).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["email"],
          message: t("admin.users.validation.emailInvalid"),
        });
      }
    });

export type AdminUserFormData = z.infer<ReturnType<typeof createAdminUserSchema>>;

export type CreateAdminUserSubmitData = {
  name: string;
  email: string;
  phone: string;
  department?: string;
  position?: string;
  userType?: AdminUserType;
};

export function isTellusEmailModeForFormData(
  data: Pick<AdminUserFormData, "userType">,
  forceTellusEmailMode: boolean,
): boolean {
  return forceTellusEmailMode || data.userType === "tellus";
}

export function resolveCreateAdminUserEmail(
  data: AdminUserFormData,
  forceTellusEmailMode: boolean,
): string {
  if (isTellusEmailModeForFormData(data, forceTellusEmailMode)) {
    return clampTellusEmailValue(data.email ?? "");
  }
  return data.email!.trim();
}

export function mapAdminUserFormToCreateRequest(
  data: AdminUserFormData,
  options: {
    forceTellusEmailMode: boolean;
    includeUserTypeInPayload: boolean;
  },
): CreateAdminUserSubmitData {
  return {
    name: data.name,
    email: resolveCreateAdminUserEmail(data, options.forceTellusEmailMode),
    phone: data.phone,
    department: data.department || undefined,
    position: data.position || undefined,
    ...(options.includeUserTypeInPayload && data.userType
      ? { userType: data.userType }
      : {}),
  };
}
